/**
 * src/data/importMapper.js — single source of truth for CSV column auto-mapping.
 *
 * Used by:
 *   • src/pages/ListsPage.js       (main "Import Contacts" UI)
 *   • src/pages/OnboardingPage.js  (onboarding wizard CSV import)
 *   • src/pages/LinkedInPage.js    (LinkedIn Sales Nav CSV import)
 *
 * Replaces three separate per-flow mappers that all suffered from:
 *   • "first substring match wins" → "Company Size" auto-mapped to Company
 *   • no bipartite assignment → two CSV columns silently mapped to the same field
 *   • no content sampling → no way to disambiguate header collisions
 *
 * Algorithm:
 *   1. For every (column, field) pair, score the header match (exact id /
 *      exact alias / whole-word alias / substring fallback).
 *   2. Add a bonus from the field's content validator if it accepts the
 *      column's sample values.
 *   3. Sort all candidate (col, field, score) tuples by score desc.
 *   4. Greedy bipartite assignment: walk the sorted list; assign each
 *      pair only if neither column nor field is already claimed.
 *      A column maps to at most one field; a field is filled by at most
 *      one column. Tuples below `threshold` are skipped — those columns
 *      land on "skip" by absence from the result map.
 */

// ── Canonical import field list ──
// Single source of truth across all import flows. Field ids match the
// CoreFlow contact object's local-state shape (fn, ln, ph, etc.).
export const IMPORT_FIELDS = [
  {
    id: "fn",
    label: "First Name",
    aliases: ["first name", "firstname", "first_name", "given name", "fname", "first"],
    // Content validator: short single words, no digits/@, mostly one token.
    validator: (samples) => {
      if (!samples || samples.length === 0) return false;
      const ok = samples.filter(s =>
        typeof s === "string" &&
        s.trim().length > 0 &&
        s.trim().length < 30 &&
        !/[\d@,]/.test(s) &&
        !s.includes(" ")
      ).length;
      return ok / samples.length > 0.7;
    },
  },
  {
    id: "ln",
    label: "Last Name",
    aliases: ["last name", "lastname", "last_name", "surname", "lname", "last", "family name"],
    validator: (samples) => {
      if (!samples || samples.length === 0) return false;
      const ok = samples.filter(s =>
        typeof s === "string" &&
        s.trim().length > 0 &&
        s.trim().length < 50 &&
        !/[\d@]/.test(s)
      ).length;
      return ok / samples.length > 0.7;
    },
  },
  {
    id: "email",
    label: "Email",
    aliases: ["email", "email address", "e-mail", "emailaddress", "primary email", "work email"],
    validator: (samples) => {
      if (!samples || samples.length === 0) return false;
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const ok = samples.filter(s => typeof s === "string" && re.test(s.trim())).length;
      return ok / samples.length > 0.5;
    },
  },
  {
    id: "phone",
    label: "Phone",
    aliases: ["phone", "phone number", "phonenumber", "mobile", "telephone", "cell", "work phone", "primary phone", "tel"],
    validator: (samples) => {
      if (!samples || samples.length === 0) return false;
      const ok = samples.filter(s => {
        if (typeof s !== "string") return false;
        const stripped = s.replace(/[\s\-\(\)\+\.]/g, "");
        return stripped.length >= 7 && /^\d+$/.test(stripped);
      }).length;
      return ok / samples.length > 0.5;
    },
  },
  {
    id: "company",
    label: "Company",
    // Header collision danger: bare alias "company" matches "Company Size",
    // "Company Industry", "Company Domain" etc. The scoring algorithm relies
    // on exact-alias / whole-word matches outranking substring fallbacks.
    aliases: ["company", "company name", "companyname", "organization", "org", "account", "account name", "employer"],
    validator: (samples) => {
      if (!samples || samples.length === 0) return false;
      const ok = samples.filter(s =>
        typeof s === "string" &&
        s.trim().length > 1 &&
        !/^\d+[-–]\d+/.test(s) &&        // NOT a range like "11-50"
        !/^\d+\+?$/.test(s) &&            // NOT a single number/range like "10001+"
        !/^(self|self-employed|n\/a|none)$/i.test(s.trim())
      ).length;
      return ok / samples.length > 0.5;
    },
  },
  {
    id: "title",
    label: "Job Title",
    aliases: ["title", "job title", "jobtitle", "job_title", "position", "role", "designation", "job", "headline", "current title", "current position"],
  },
  {
    id: "city",
    label: "City",
    aliases: ["city", "town"],
  },
  {
    id: "state",
    label: "State",
    aliases: ["state", "region", "province", "state/region", "state_region"],
  },
  {
    id: "industry",
    label: "Industry",
    aliases: ["industry", "sector", "vertical"],
  },
  {
    id: "source",
    label: "Lead Source",
    aliases: ["source", "lead source", "leadsource", "lead_source", "how did you hear", "origin", "channel"],
  },
  {
    id: "status",
    label: "Status",
    aliases: ["status", "lead status", "contact status", "lifecycle"],
  },
  {
    id: "score",
    label: "Lead Score",
    aliases: ["score", "lead score", "leadscore", "rating"],
  },
  {
    id: "tags",
    label: "Tags",
    aliases: ["tags", "labels", "categories"],
  },
  {
    id: "linkedin_url",
    label: "LinkedIn URL",
    aliases: ["linkedin", "linkedin url", "linkedin_url", "profile url", "profile_url"],
  },
];

// ── Scoring helpers ──

// Tokenize a normalized header on whitespace / underscores / hyphens.
// "Company Name" → ["company", "name"]. "company_size" → ["company", "size"].
function tokenize(s) {
  return (s || "").split(/[\s_\-]+/).filter(Boolean);
}

// Score a single (header, field) pair. Higher = better match.
// Range:
//   100  exact id match            ("fn" header → fn field)
//    95  exact alias match         ("first name" header → fn field)
//    90  exact label match         ("First Name" header → fn field)
//    85  full tokenized match      (alias matches all header tokens, in order)
//    75  alias is most of header   ("Company Name" → company field, mostly company)
//    50  alias is a word in header ("Company Size" → company field, but "size" is extra)
//    35  alias is substring        ("companyname" → company field via includes)
//     0  no match
function scoreMatch(header, field) {
  const h = (header || "").toLowerCase().trim();
  if (!h) return 0;

  // Exact id match.
  if (h === field.id) return 100;

  // Exact alias match.
  if (field.aliases.some(a => h === a)) return 95;

  // Exact label match (label is canonical display, e.g. "First Name").
  if (h === field.label.toLowerCase()) return 90;

  // Tokenized whole-word matching.
  const headerTokens = tokenize(h);
  // Try longest aliases first so multi-word aliases beat bare words.
  const sortedAliases = [...field.aliases].sort((a, b) => b.length - a.length);

  for (const alias of sortedAliases) {
    const aliasTokens = tokenize(alias);
    if (aliasTokens.length === 0) continue;

    // Full match: every header token IS the alias, in order.
    if (
      aliasTokens.length === headerTokens.length &&
      aliasTokens.every((t, i) => t === headerTokens[i])
    ) {
      return 85;
    }

    // Subsequence match: alias appears as consecutive tokens within a longer header.
    if (aliasTokens.length < headerTokens.length) {
      for (let i = 0; i <= headerTokens.length - aliasTokens.length; i++) {
        if (aliasTokens.every((t, j) => t === headerTokens[i + j])) {
          // Penalize by how much of the header is "extra" tokens.
          const aliasLen = aliasTokens.join("").length;
          const headerLen = headerTokens.join("").length;
          if (aliasLen / headerLen >= 0.7) return 75; // mostly the alias
          return 50;                                   // alias is a word, but header has more
        }
      }
    }
  }

  // Substring fallback. Lowest priority — catches "companyname" against "company"
  // when tokenization didn't split. Loses to whole-word matches by design.
  for (const alias of sortedAliases) {
    if (h.includes(alias)) return 35;
  }

  return 0;
}

// Apply content validator if present. Returns score boost (+30 on match).
function validateContent(field, sampleValues) {
  if (!field.validator) return 0;
  if (!sampleValues || sampleValues.length === 0) return 0;
  return field.validator(sampleValues) ? 30 : 0;
}

// ── Public API ──

/**
 * Auto-map CSV columns to CoreFlow fields with bipartite assignment.
 *
 * @param {string[]} headers — CSV column headers in order
 * @param {Array<Array|Object>} sampleRows — optional, first ~20 data rows
 *   for content sniffing. Rows can be arrays (indexed by colIdx) or objects
 *   keyed by header name. Empty / missing → falls back to header-only scoring.
 * @param {Array} customFields — optional, custom field definitions to extend
 *   the canonical IMPORT_FIELDS list. Each {id, label} becomes a candidate.
 * @param {number} threshold — minimum total score to auto-assign. Default 50.
 *   Pairs below this end up unassigned (column maps to "skip" / absent).
 *
 * @returns {Object<number, string>} — { [colIdx]: fieldId } map. A column not
 *   present in the result is unmapped (skip). Field ids match IMPORT_FIELDS
 *   plus custom_<id> for custom fields.
 */
export function autoMapColumns(headers, sampleRows = [], customFields = [], threshold = 50) {
  if (!headers || headers.length === 0) return {};

  // Build the candidate field pool: canonical + custom.
  const allFields = [
    ...IMPORT_FIELDS,
    ...(customFields || []).map(cf => ({
      id: "custom_" + cf.id,
      label: cf.label,
      aliases: [
        (cf.label || "").toLowerCase(),
        (cf.id || "").toLowerCase(),
      ].filter(Boolean),
    })),
  ];

  // Score every (column, field) pair.
  const scores = []; // [{ colIdx, fieldId, score }]
  headers.forEach((header, colIdx) => {
    // Collect up to 10 non-empty samples from the column for content sniffing.
    const samples = [];
    for (const row of (sampleRows || []).slice(0, 20)) {
      let value;
      if (Array.isArray(row)) {
        value = row[colIdx];
      } else if (row && typeof row === "object") {
        value = row[header] || row[(header || "").toLowerCase()];
      }
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        samples.push(String(value).trim());
      }
      if (samples.length >= 10) break;
    }

    for (const field of allFields) {
      const headerScore = scoreMatch(header, field);
      if (headerScore === 0) continue; // not a candidate at all
      const contentScore = validateContent(field, samples);
      scores.push({ colIdx, fieldId: field.id, score: headerScore + contentScore });
    }
  });

  // Greedy bipartite assignment: highest score first, one column per field.
  scores.sort((a, b) => b.score - a.score);
  const assignedFields = new Set();
  const assignedColumns = new Set();
  const result = {};

  for (const { colIdx, fieldId, score } of scores) {
    if (score < threshold) break; // sorted desc — all remaining are below threshold
    if (assignedFields.has(fieldId)) continue;
    if (assignedColumns.has(colIdx)) continue;
    result[colIdx] = fieldId;
    assignedFields.add(fieldId);
    assignedColumns.add(colIdx);
  }

  return result;
}
